//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { EthAddr, TxHash } from '@shared/SchemaTypes'
import { Contract, Signer } from 'ethers'

import { ActivityEnum, AlertLevelEnum, PoolTypeEnum, RoleEnum } from '@shared/Enums'

import { poolActions } from '@react/redux/poolSlice'

import { logOT } from '@shared/loggingShared'

import { ThunkAction, UnknownAction } from '@reduxjs/toolkit'
import { IPoolBaseAbi, LYTPoolControllerAbi, LYTPoolAbi } from '@shared/Abis'

import { dispatchError, OtStoreState } from '@react/redux/store'

import { otAssert } from '@shared/conversions'

import { alertActions } from '@react/redux/alertSlice'
import { ETH_ADDR_ZERO } from '@shared/SchemaTypes'
import { txUUID, wsServerActionPoolTX, wsServerActionPoolTXRequest } from '@shared/WsServerActions'
import { UserConnection } from '@shared/DeploymentConfigTypes'

export const lytSetIndicativeInterestRateTx =
  (props: {
    poolAddr: EthAddr
    indicativeInterestRate: string
    collateralRate: string
  }): ThunkAction<void, OtStoreState, unknown, UnknownAction> =>
  async (dispatch, getState): Promise<void> => {
    const { poolAddr, indicativeInterestRate, collateralRate } = props
    // TODO: Change this for a correct new activity
    const activity = ActivityEnum.POOL_ACTIVATE
    try {
      logOT.debug(activity)

      const { webSocket } = getState().chainState
      const { chainConfigurationName } = getState().chainState.chainConfiguration
      const { roleEnum, signer } = getState().chainState.userConnection as UserConnection
      otAssert(roleEnum == RoleEnum.ISSUER || roleEnum == RoleEnum.PROTOCOL_MANAGER)
      otAssert(signer, 'signer is not defined')

      await lytSetIndicativeInterestRate(
        webSocket,
        signer as Signer,
        poolAddr,
        indicativeInterestRate,
        collateralRate,
        chainConfigurationName
      )

      dispatch(poolActions.setIndicativeInterestRateSuccess({ poolAddr }))
    } catch (e: any) {
      dispatch(poolActions.setIndicativeInterestRateFail({}))
      dispatch(
        alertActions.addAlert({
          title: 'Could not set indicative interest rate.',
          description: 'Please wait and try again',
          createdAt: Date.now() / 1000,
          dueDateTime: Date.now() / 1000,
          txHash: '',
          sourceObjectAddr: ETH_ADDR_ZERO,
          alertLevel: AlertLevelEnum.ERROR,
        })
      )
      await dispatchError(dispatch, e, activity, 1037)
    }
  }

export const lytSetIndicativeInterestRate = async (
  webSocket: WebSocket | undefined,
  poolAdminSigner: Signer,
  poolAddr: EthAddr,
  indicativeInterestRate: string,
  collateralRate: string,
  chainConfigurationName: string
): Promise<void> => {
  logOT.debug(lytSetIndicativeInterestRate.name)
  const uuid = txUUID()
  webSocket?.send(
    wsServerActionPoolTXRequest(ActivityEnum.EXCHANGE_RATE_UPDATE, poolAddr, uuid, chainConfigurationName)
  )

  const poolBaseContract = new Contract(poolAddr, IPoolBaseAbi, poolAdminSigner)
  const poolType = Number(await poolBaseContract.poolType())

  if (poolType === PoolTypeEnum.Lyt) {
    const poolContract = new Contract(poolAddr, LYTPoolAbi, poolAdminSigner) as any
    const poolControllerAddr = await poolContract.lytPoolController()

    otAssert(poolControllerAddr)

    const poolControllerContract = new Contract(poolControllerAddr, LYTPoolControllerAbi, poolAdminSigner) as any

    const tx2 = await poolControllerContract
      .connect(poolAdminSigner)
      .setIndicativeAndCollateralRates(indicativeInterestRate, collateralRate)

    webSocket?.send(
      wsServerActionPoolTX(
        ActivityEnum.EXCHANGE_RATE_UPDATE,
        poolAddr,
        uuid,
        tx2.hash as TxHash,
        chainConfigurationName
      )
    )

    await tx2.wait(1)
  } else {
    throw new Error('Unknown pool type')
  }
}
